import type { components } from '@/services/api-allo/api-allo-v1';

/**
 * Power Dialer domain types.
 *
 * `SessionContact` IS a `Contact` from the API (not a wrapper) plus a
 * session-specific `dialing_state`. This guarantees any cell/handler that
 * works in the Contacts view also works here once the backend lands —
 * the only swap is the data source.
 */

export type DialingState =
  | 'pending'
  | 'ringing'
  | 'connected'
  | 'no-answer'
  | 'voicemail'
  | 'skipped';

export type Contact = components['schemas']['Contact'];

/**
 * Subset of `CallResponse.routing_result` values relevant to outbound
 * Power Dialer rows. Mirrors the enum on `QueueNumberResponse` from the
 * remote API.
 */
export type QueueRoutingResult =
  | 'FAILED'
  | 'RECEIVED'
  | 'BLOCKED'
  | 'ANSWERED'
  | 'TRANSFERRED_BLAND'
  | 'TRANSFERRED_ELEVEN_LABS'
  | 'TRANSFERRED_EXTERNAL'
  | 'FAILED_EXTERNAL'
  | 'VOICEMAIL'
  | 'CLOSED';

export type QueueNotReachedReason =
  | 'BUSY'
  | 'CALLEE_TEMPORARILY_UNAVAILABLE'
  | 'RING_TIMEOUT'
  | 'NOT_IN_SERVICE'
  | 'DECLINED'
  | 'NETWORK_ERROR';

export type SessionContact = Contact & {
  /** Stable identity for this entry inside the current queue. */
  queue_item_id?: string;
  /** Position of this entry inside the current queue (used as the delete key). */
  queue_position?: number;
  /** Per-session dialing state, layered on top of the canonical Contact. */
  dialing_state: DialingState;
  /** Whether the AI summary has been generated for this contact's call. */
  has_summary?: boolean;
  /** Call associated with this queue number, when it has already been called. */
  call_id?: string;
  /** Start date for the associated call, when supplied by the queue API. */
  call_start_date?: string;
  /** Outcome enum from `/v1/dialing-queues/current`. Drives the row preview. */
  routing_result?: QueueRoutingResult;
  /** Closed-call sub-reason — only meaningful when `routing_result === 'CLOSED'`. */
  not_reached_destination_reason?: QueueNotReachedReason;
  /** True when the system detected a voicemail beep on the answered leg. */
  detected_outbound_voicemail?: boolean;
  /** True when the agent left a voicemail message. */
  voicemail_left?: boolean;
};

/**
 * Snapshot of a completed session, captured before the reducer resets.
 * Used to render the end-of-session summary screen and to build the
 * CubeJS query that fetches aggregate metrics for the session.
 */
export type EndedSessionContext = {
  session_id: string;
  queue_id: string | null;
  started_at: string;
  ended_at: string;
  user_name?: string;
  line_name: string;
  line_number: string;
  contacts: SessionContact[];
  dialing_state: Record<string, DialingState>;
};

export const getSessionContactKey = (
  contact: Pick<SessionContact, 'queue_item_id' | 'id' | 'numbers'>,
): string => contact.queue_item_id ?? contact.id ?? contact.numbers?.[0] ?? '';

/**
 * Queue entries without a matched contact get a synthetic `queue-…` id from
 * `usePowerDialerContacts` so the reducer can track per-position state. These
 * are not real backend contact ids and must never reach `/v1/contact/{id}`.
 * A resolved id is a real backend contact we can read/patch.
 */
export const isResolvedContactId = (id: string | undefined): id is string =>
  !!id && !id.startsWith('queue-');
